import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Login.css";
import "./Policy.css";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <Link to="/login" className="policy-back">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M19 12H5M12 5l-7 7 7 7"/>
        </svg>
        Back to Login
      </Link>

      <div className="centered-logo-container">
        <div className="logo-wrapper">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="site-logo-img" />
        </div>
      </div>

      <div className="policy-card">
        <h1>Privacy Policy</h1>
        <p className="policy-effective">Effective Date: March 3, 2026</p>

        <hr className="policy-divider" />

        <div className="policy-section">
          <h2>1. Introduction</h2>
          <p>
            Masters Path Consulting LLC ("we," "us," or "our") operates the Masters Path Performance Dashboard
            (the "Platform"), a web-based performance management application for powersports dealerships. This
            Privacy Policy explains what information we collect, how we use it, and how we protect it when you
            or your organization uses the Platform.
          </p>
          <p>
            By using the Platform, you agree to the collection and use of information as described in this policy.
          </p>
        </div>

        <div className="policy-section">
          <h2>2. Information We Collect</h2>
          <p><strong style={{color: 'rgba(220,235,255,0.85)', fontWeight: 600}}>Account Information</strong></p>
          <p>When you are granted access to the Platform, we collect:</p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number (optional)</li>
            <li>Username</li>
            <li>Password (stored as a one-way cryptographic hash—never in plaintext)</li>
            <li>Assigned role and dealership affiliations</li>
          </ul>

          <p style={{marginTop: 16}}><strong style={{color: 'rgba(220,235,255,0.85)', fontWeight: 600}}>Dealership Business Data</strong></p>
          <p>Data entered into the Platform by you or your organization, including:</p>
          <ul>
            <li>Sales records, deal activity, and customer information (first name, last name)</li>
            <li>Finance and insurance (F&I) product data</li>
            <li>Service department metrics, repair order data, and technician performance</li>
            <li>Parts and apparel sales records</li>
            <li>Inventory data for motorcycles, parts, and apparel</li>
            <li>Performance targets and monthly goals</li>
            <li>Daily floor activity logs</li>
            <li>Net Promoter Score (NPS) metrics</li>
          </ul>

          <p style={{marginTop: 16}}><strong style={{color: 'rgba(220,235,255,0.85)', fontWeight: 600}}>Usage Information</strong></p>
          <p>We may automatically collect certain technical information when you use the Platform, including:</p>
          <ul>
            <li>IP address and browser type</li>
            <li>Pages visited and features accessed</li>
            <li>Session timestamps and duration</li>
            <li>Error logs and diagnostic data</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, operate, and maintain the Platform</li>
            <li>Authenticate users and enforce role-based access controls</li>
            <li>Display dealership performance data, analytics, and reports</li>
            <li>Respond to support requests and technical inquiries</li>
            <li>Diagnose technical issues and improve Platform performance</li>
            <li>Send important service-related communications (e.g., account or security notices)</li>
            <li>Comply with applicable legal obligations</li>
          </ul>
          <p>
            We do not use your data for advertising, marketing profiling, or any purpose unrelated to
            operating and improving the Platform.
          </p>
        </div>

        <div className="policy-section">
          <h2>4. Data Sharing</h2>
          <p>
            We do not sell, rent, or trade your personal information or dealership data to third parties.
            We may share information only in the following limited circumstances:
          </p>
          <ul>
            <li>
              <strong>Service Providers:</strong> With trusted vendors or contractors who assist in
              operating the Platform (such as cloud hosting or database services). These parties are
              bound by confidentiality obligations and are not permitted to use your data for their
              own purposes.
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required to comply with applicable law, a court
              order, subpoena, or lawful request from a governmental authority.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of
              business assets. In such cases, we will provide reasonable notice and ensure confidentiality
              protections are maintained.
            </li>
            <li>
              <strong>With Your Consent:</strong> In any other circumstance where you have expressly
              authorized the disclosure.
            </li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>5. Data Security</h2>
          <p>
            We take the security of your information seriously and implement industry-standard safeguards,
            including:
          </p>
          <ul>
            <li>Encrypted data transmission via HTTPS/TLS on all connections</li>
            <li>Passwords hashed using bcrypt (one-way, salted hashing—plaintext is never stored)</li>
            <li>HTTP-only, Secure, SameSite session cookies to protect authentication tokens</li>
            <li>Role-based access controls that limit data access to authorized users only</li>
            <li>Dealership-scoped data isolation, ensuring users can only access the dealerships they are assigned to</li>
          </ul>
          <p>
            While we work to protect your information, no method of transmission over the internet or
            electronic storage is completely secure. We cannot guarantee absolute security, but we will
            promptly notify affected users in the event of a confirmed data breach that may affect their
            personal information.
          </p>
        </div>

        <div className="policy-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your account information and dealership data for as long as your account is active
            or as needed to provide the service. If your account is closed or access is terminated, we
            will retain your data for a reasonable period as required for legal, accounting, or audit
            purposes. After that period, your data will be deleted or anonymized.
          </p>
          <p>
            You may request earlier deletion of your personal information by contacting us (see Section 12).
            Note that some information may be retained where we have a legal obligation to do so.
          </p>
        </div>

        <div className="policy-section">
          <h2>7. Your Rights</h2>
          <p>Depending on your location and applicable law, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate or incomplete information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to or restrict certain processing of your information</li>
            <li>Request a copy of your data in a portable format</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{" "}
            <a href="mailto:hb.masterspath@gmail.com" style={{color: '#ffb57a'}}>hb.masterspath@gmail.com</a>.
            We will respond to verified requests within a reasonable timeframe.
          </p>
        </div>

        <div className="policy-section">
          <h2>8. Cookies</h2>
          <p>
            The Platform uses session cookies to maintain your authenticated session between page loads.
            These cookies are:
          </p>
          <ul>
            <li><strong>HTTP-only:</strong> Not accessible by JavaScript, reducing the risk of cross-site scripting attacks.</li>
            <li><strong>Secure:</strong> Transmitted only over HTTPS connections in production.</li>
            <li><strong>Session-scoped:</strong> Expire after 7 days or immediately upon logout.</li>
          </ul>
          <p>
            We do not use third-party advertising cookies, tracking pixels, or behavioral analytics tools.
            No data is shared with advertising networks.
          </p>
        </div>

        <div className="policy-section">
          <h2>9. Children's Privacy</h2>
          <p>
            The Platform is intended for business use by adults and is not directed to individuals under
            18 years of age. We do not knowingly collect personal information from minors. If you believe
            a minor has provided us with personal information, please contact us so we can promptly
            remove it.
          </p>
        </div>

        <div className="policy-section">
          <h2>10. Third-Party Links</h2>
          <p>
            The Platform may contain links to external websites or services not operated by us. We are
            not responsible for the privacy practices of those third parties and encourage you to review
            their privacy policies before providing any personal information.
          </p>
        </div>

        <div className="policy-section">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technology, or legal requirements. We will notify users of material changes by updating the
            effective date at the top of this page and, where appropriate, by posting a notice within the
            Platform. We encourage you to review this policy periodically. Your continued use of the
            Platform after changes are posted constitutes acceptance of the updated policy.
          </p>
        </div>

        <div className="policy-section">
          <h2>12. Governing Law</h2>
          <p>
            This Privacy Policy is governed by the laws of the State of Wisconsin. Any disputes relating
            to this policy shall be subject to the jurisdiction of the state and federal courts located
            in Wisconsin.
          </p>
        </div>

        <div className="policy-section">
          <h2>13. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us:
          </p>
          <div className="policy-contact-block">
            <p>
              <strong style={{color: 'rgba(220,235,255,0.92)'}}>Masters Path Consulting LLC</strong><br />
              <a href="mailto:hb.masterspath@gmail.com">hb.masterspath@gmail.com</a>
            </p>
          </div>
        </div>
      </div>

      <div className="policy-footer">
        <span>© {new Date().getFullYear()} Masters Path Consulting LLC</span>
        <span className="footer-sep">·</span>
        <Link to="/terms">Terms of Service</Link>
        <span className="footer-sep">·</span>
        <Link to="/account-deletion">Account Deletion</Link>
        <span className="footer-sep">·</span>
        <Link to="/login">Sign In</Link>
      </div>
    </div>
  );
}
